import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import DatePicker, { CalendarContainer } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import calendarIcon from "../../../src/assests/images/calendar.svg";
import arrowLeft from "../../../src/assests/images/arrow-left.svg";
import arrowRight from "../../../src/assests/images/arrow-right.png";
import doubleArrowLeft from "../../../src/assests/images/double-arrow-left.svg";
import doubleArrowRight from "../../../src/assests/images/double-arrow-right.svg";
import { useNavigate, useParams } from "react-router-dom";

interface DatePickersProps {
  selectedDate1: Date | null;
  setSelectedDate1: Dispatch<SetStateAction<Date | null>>;
}

export default function DatePickersCohort({
  selectedDate1,
  setSelectedDate1,
}: DatePickersProps) {
  const selectedDateRef = useRef<Date | null>(null);
  const { id } = useParams();
  const navigate = useNavigate();

  // Track which field is active for ▲ / ▼
  const [activeField, setActiveField] = useState<"hours" | "minutes" | "seconds" | "ampm">("hours");
  // State to force DatePicker re-render
  const [pickerKey, setPickerKey] = useState(0);

  // Set default datetime as current
  useEffect(() => {
    if (!selectedDate1) {
      setSelectedDate1(new Date());
    }
  }, [selectedDate1, setSelectedDate1]);

  // Extract time values
  const hours = selectedDate1 ? selectedDate1.getHours() : 0;
  const minutes = selectedDate1 ? selectedDate1.getMinutes() : 0;
  const seconds = selectedDate1 ? selectedDate1.getSeconds() : 0;

  // Update time values
  const updateTime = (h: number, m: number, s: number) => {
    if (!selectedDate1) return;
    let newDate = new Date(selectedDate1);
    newDate.setHours(h, m, s);
    setSelectedDate1(newDate);
  };

  // Handle arrow clicks
  const handleArrow = (dir: "up" | "down") => {
    let h = hours,
      m = minutes,
      s = seconds;

    if (activeField === "hours") {
      h = dir === "up" ? (h + 1) % 24 : (h - 1 + 24) % 24;
    } else if (activeField === "minutes") {
      m = dir === "up" ? (m + 1) % 60 : (m - 1 + 60) % 60;
    } else if (activeField === "seconds") {
      s = dir === "up" ? (s + 1) % 60 : (s - 1 + 60) % 60;
    }

    updateTime(h, m, s);
  };

  // Custom container with time + arrows + footer
  const MyContainer = ({ className, children }: any) => {
    return (
      <div className={className}>
        <CalendarContainer className={className}>{children}</CalendarContainer>
        <style>
          {`
        .react-datepicker-popper {
          transform: translate(0px, -50px) !important;
        }
      `}
        </style>
        {/* Timer Section */}
        <div
          style={{
            padding: "0 10px 10px",
            marginTop: "5px",
          }}
        >
          <span style={{ fontWeight: "bold", width: "40px" }}>Time</span>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            {/* Hours */}
            <input
              type="text"
              value={hours.toString().padStart(2, "0")}
              onFocus={() => setActiveField("hours")}
              readOnly
              style={{
                width: "40px",
                textAlign: "center",
                height: "25px",
                border: activeField === "hours" ? "2px solid #F14B56" : "1px solid #ccc",
                borderRadius: "5px",
              }}
            />
            :
            {/* Minutes */}
            <input
              type="text"
              value={minutes.toString().padStart(2, "0")}
              onFocus={() => setActiveField("minutes")}
              readOnly
              style={{
                width: "40px",
                textAlign: "center",
                height: "25px",
                border: activeField === "minutes" ? "2px solid #F14B56" : "1px solid #ccc",
                borderRadius: "5px",
              }}
            />
            :
            {/* Seconds */}
            <input
              type="text"
              value={seconds.toString().padStart(2, "0")}
              onFocus={() => setActiveField("seconds")}
              readOnly
              style={{
                width: "40px",
                textAlign: "center",
                height: "25px",
                border: activeField === "seconds" ? "2px solid #F14B56" : "1px solid #ccc",
                borderRadius: "5px",
              }}
            />
            {/* No AM/PM input anymore */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <button
                onClick={() => handleArrow("up")}
                style={{
                  border: "none",
                  padding: "2px",
                  cursor: "pointer",
                  background: "none",
                  height: "16px",
                }}
              >
                <img
                  src={arrowLeft}
                  alt="up"
                  style={{ transform: "rotate(90deg)" }}
                />
              </button>
              <button
                onClick={() => handleArrow("down")}
                style={{
                  border: "none",
                  padding: "2px",
                  cursor: "pointer",
                  background: "none",
                  height: "16px",
                }}
              >
                <img src={arrowLeft} alt="prev month" style={{ transform: "rotate(-90deg)" }} />
              </button>
            </div>
          </div>

          {/* Footer Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "10px",
              borderTop: "1px solid rgb(228 228 228)",
              paddingTop: "10px",
            }}
          >
            <button
              onClick={() => {
                setSelectedDate1(null); // Clear the selected date
                setPickerKey((prev) => prev + 1); // Force DatePicker re-render
              }}
              style={{
                background: "none",
                border: "none",
                color: "#F14B56",
                cursor: "pointer",
              }}
            >
              Clear
            </button>
            <button
              onClick={() => setSelectedDate1(new Date())}
              style={{
                background: "none",
                border: "none",
                color: "#F14B56",
                cursor: "pointer",
              }}
            >
              Today
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <DatePicker
        key={pickerKey} // Add key to force re-render
        selected={selectedDate1}
        onChange={(date) => setSelectedDate1(date)}
        dateFormat="dd/MM/yyyy - HH:mm:ss"
        placeholderText="Select Date & Time"
        minDate={new Date()}
        calendarContainer={MyContainer}
        popperPlacement="bottom-start"
        customInput={
          <input
            style={{
              width: "400px",
              border: "1px solid #c7c7c7",
              borderRadius: "8px",
              boxSizing: "border-box",
              fontSize: "16px",
              marginTop: "10px",
              padding: "14px 35px 14px 20px",
              backgroundImage: `url(${calendarIcon})`,
              backgroundPosition: "right 10px center",
              backgroundRepeat: "no-repeat",
            }}
            placeholder="Select Date & Time"
          />
        }
        renderCustomHeader={({
          date,
          decreaseMonth,
          increaseMonth,
          decreaseYear,
          increaseYear,
        }) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <button onClick={decreaseYear}>
              <img src={doubleArrowLeft} alt="prev year" />
            </button>
            <button onClick={decreaseMonth}>
              <img src={arrowLeft} alt="prev month" />
            </button>
            <span style={{ margin: "0 10px" }}>
              {date.toLocaleString("default", {
                year: "numeric",
                month: "long",
              })}
            </span>
            <button onClick={increaseMonth}>
              <img src={arrowRight} alt="next month" />
            </button>
            <button onClick={increaseYear}>
              <img src={doubleArrowRight} alt="next year" />
            </button>
          </div>
        )}
      />
    </div>
  );
}